/**
 * ShoppersLanding — cyannect.com/shoppers marketing page for consumers.
 *
 * Cyannect.com itself is the business platform; the shopping experience
 * lives inside the Cyannect mobile app. This page tells that story +
 * hands off to the app-store badges (which stay "Coming soon" until real
 * store URLs are live) or the working PWA (`app.cyannect.com`).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "qrcode";
import {
  Apple, Play, Smartphone, ArrowRight, QrCode, Store, Sparkles,
  Search, MousePointerClick, ShoppingBag, PiggyBank, Gift, Tag,
  BadgePercent, Zap, Building2, MapPin, ChevronDown,
  Wallet, Clock, TrendingUp, CheckCircle2, Award, Handshake,
} from "lucide-react";
import Seo from "@/components/Seo";
import AdTrafficGate from "@/components/AdTrafficGate";
import PwaInstallNudge from "@/components/PwaInstallNudge";
import { urlOnHost } from "@/lib/host";
import { useShoppersI18n } from "@/lib/shoppersI18n";

const APP_URL = urlOnHost("app", "/coupons");

export default function ShoppersLanding() {
  const [qr, setQr] = useState(null);
  const { lang, setLang, t, faq } = useShoppersI18n();
  useEffect(() => {
    QRCode.toDataURL(APP_URL, {
      margin: 1, width: 220,
      color: { dark: "#0e7490", light: "#ffffff" },
    }).then(setQr).catch(() => setQr(null));
  }, []);
  useEffect(() => {
    // Broadcast the active language to the html tag + open-graph meta so
    // Google's SERP + share cards render the right locale.
    try { document.documentElement.setAttribute("lang", lang); } catch {}
  }, [lang]);

  return (
    <div className="relative" data-testid="shoppers-landing" data-lang={lang}>
      <Seo title={lang === "fr"
                    ? "Magasinez, économisez et gagnez des récompenses — Cyannect"
                    : "Shop, save & get rewarded — Cyannect for shoppers"}
           description={lang === "fr"
                    ? "Découvrez les offres des marques participantes, activez des coupons et gagnez des récompenses Cyannect sur les achats admissibles — le tout depuis l'application mobile Cyannect."
                    : "Discover offers from participating brands, activate coupons, and earn Cyannect Rewards on eligible purchases — all from the Cyannect mobile app."} />
      <AdTrafficGate />
      <PwaInstallNudge />

      {/* Language toggle */}
      <div className="fixed top-20 right-4 z-30 flex items-center gap-1 rounded-full bg-white/90 backdrop-blur border border-slate-200 p-1 shadow-sm text-xs font-semibold"
           data-testid="lang-toggle">
        {["en", "fr"].map((code) => (
          <button key={code}
                  onClick={() => setLang(code)}
                  className={`px-2.5 py-1 rounded-full transition ${lang === code ? "bg-cyan-500 text-white" : "text-slate-500 hover:text-slate-900"}`}
                  data-testid={`lang-toggle-${code}`}>
            {code.toUpperCase()}
          </button>
        ))}
      </div>

      {/* ========================= HERO ========================= */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-32 -right-16 w-[38rem] h-[38rem] rounded-full bg-cyan-300/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 w-[32rem] h-[32rem] rounded-full bg-emerald-300/25 blur-3xl" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 md:py-24 grid grid-cols-1 md:grid-cols-[1.15fr,1fr] gap-10 items-center">
          <div>
            <div className="pf-chip pf-chip-cyan w-fit" data-testid="hero-tag">
              <Sparkles className="w-3 h-3" /> {t("for_shoppers")}
            </div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900">
              {t("hero_h1_a")} <span className="pf-gradient-text">{t("hero_h1_b")}</span>
            </h1>
            <p className="mt-5 text-base text-slate-600 leading-relaxed max-w-xl">
              {t("hero_p1")}
            </p>
            <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-xl">
              {t("hero_p2")}
            </p>
            <div className="mt-4 p-3 rounded-xl bg-amber-50/70 border border-amber-200/60 text-xs text-slate-700 max-w-xl leading-relaxed"
                 data-testid="hero-note">
              {t("hero_note")}
            </div>
            <div className="mt-6 flex flex-wrap gap-3" data-testid="hero-badges">
              <StoreBadge platform="ios" testid="hero-app-ios" />
              <StoreBadge platform="android" testid="hero-app-android" />
            </div>
          </div>
          <div className="flex justify-center md:justify-end" data-testid="hero-qr-wrap">
            <QrCard qr={qr} large caption={t("scan_download")} />
          </div>
        </div>
      </section>

      {/* ================= DISCOVER MORE WAYS TO SAVE ================= */}
      <Section testid="discover" tag="Discover More Ways to Save">
        <SectionTitle>Your Favourite Stores. Their Offers. One App.</SectionTitle>
        <p className="text-slate-600 max-w-3xl leading-relaxed mt-4">
          Finding a great offer shouldn't mean searching through endless coupon websites or trying codes that may no longer work.
        </p>
        <p className="text-slate-600 max-w-3xl leading-relaxed mt-2">
          Cyannect brings offers from participating brands and businesses together in one convenient shopping app. Each participating merchant has its own Cyannect Storefront, where you can explore available offers and choose the one that works for you.
        </p>
        <FlowRow items={[
          { icon: Search, label: "Discover" },
          { icon: MousePointerClick, label: "Choose" },
          { icon: Zap, label: "Activate" },
          { icon: ShoppingBag, label: "Shop" },
          { icon: PiggyBank, label: "Save" },
        ]} />
      </Section>

      {/* ================= BRAND STOREFRONTS ================= */}
      <Section testid="storefronts" tag="Explore Brand Storefronts" tone="mint">
        <SectionTitle>One Brand. Multiple Offers.</SectionTitle>
        <p className="text-slate-600 max-w-3xl leading-relaxed mt-4">
          Instead of showing you dozens of disconnected coupons from the same business, Cyannect organizes offers by merchant. You might see:
        </p>
        <div className="mt-8 pf-card p-6 md:p-8 max-w-md" data-testid="storefront-card-example">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-slate-900 text-white flex items-center justify-center font-display text-lg font-bold">NK</div>
            <div className="flex-1">
              <div className="font-display text-xl text-slate-900">NIKE</div>
              <div className="pf-chip pf-chip-mint mt-1"><BadgePercent className="w-3 h-3" /> Up to 25% OFF</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 mt-3">12 Active Offers</div>
          <button className="mt-4 pf-btn-primary w-full py-2 rounded-full text-xs font-semibold inline-flex items-center justify-center gap-1"
                  data-testid="storefront-example-view">
            View Store <ArrowRight className="w-3 h-3" />
          </button>
          <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
            Open the merchant's Cyannect Storefront to explore its available offers.
          </p>
        </div>
        <p className="text-slate-600 mt-8">For example:</p>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
          {[
            { pct: "15%", label: "Running Shoes" },
            { pct: "20%", label: "Selected Jackets" },
            { pct: "25%", label: "Selected Backpacks" },
            { pct: "$20", label: "$150+ Qualifying Orders", isDollar: true },
          ].map((o) => (
            <div key={o.label} className="pf-card p-4 text-center" data-testid={`example-offer-${o.pct}`}>
              <div className="font-display text-2xl text-slate-900">{o.isDollar ? o.pct : `${o.pct} OFF`}</div>
              <div className="text-[11px] text-slate-500 mt-1">{o.label}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-6 max-w-3xl leading-relaxed">Some offers may also include Cyannect Rewards. Offers shown above are examples for illustration. Actual brands, discounts and availability depend on participating merchants.</p>
      </Section>

      {/* ============ FIND THE BEST AVAILABLE OFFERS ============ */}
      <Section testid="best-offer" tag="Find the Best Available Offers">
        <SectionTitle>See the Savings Before You Open the Store.</SectionTitle>
        <p className="text-slate-600 max-w-3xl leading-relaxed mt-4">
          When a participating merchant has several offers available, Cyannect can highlight its highest available discount on the storefront card. For example:
        </p>
        <div className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 text-white font-display text-2xl shadow-lg shadow-cyan-500/30"
             data-testid="best-offer-pill">
          <BadgePercent className="w-6 h-6" /> Up to 25% OFF
        </div>
        <p className="text-slate-600 mt-6 max-w-3xl leading-relaxed">
          This helps you quickly discover stores with attractive offers. Open the storefront to see which products, categories or purchases qualify.
        </p>
        <p className="text-xs text-slate-500 mt-4 max-w-3xl leading-relaxed">
          "Up to" discounts may apply only to selected products, categories or qualifying purchases. Always review the individual offer terms.
        </p>
      </Section>

      {/* ================== HOW CYANNECT WORKS ================== */}
      <Section testid="how-it-works" tag="How Cyannect Works" tone="mint">
        <SectionTitle>Saving With Cyannect Is Simple.</SectionTitle>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              n: 1, icon: Smartphone, title: "Download the Cyannect App",
              body: "Scan the QR code or download Cyannect from your device's app store.",
              extra: <div className="mt-3 flex items-center gap-3">
                {qr && <img src={qr} alt="Scan to download" className="w-16 h-16 rounded" />}
                <div className="flex flex-col gap-2">
                  <StoreBadge platform="ios" small testid="step1-app-ios" />
                  <StoreBadge platform="android" small testid="step1-app-android" />
                </div>
              </div>,
            },
            { n: 2, icon: Search, title: "Discover Participating Stores",
              body: "Open Cyannect and browse participating brands and businesses. Discover stores and offers based on availability and applicable factors such as your selected location." },
            { n: 3, icon: Store, title: "Open a Storefront",
              body: "See an interesting offer? Open the merchant's Cyannect Storefront. Instead of seeing only one coupon, you can browse the different offers currently available from that merchant." },
            { n: 4, icon: MousePointerClick, title: "Choose Your Offer",
              body: "Select the product, category or store offer that works for you. Review discount, eligible products, minimum purchase where applicable, expiration, location eligibility, redemption instructions, Cyannect Rewards where applicable, and offer terms." },
            { n: 5, icon: Zap, title: "Activate Your Offer",
              body: 'Tap "Get Offer" or "Reveal Offer". Cyannect activates the offer for you. Some offers may have a limited redemption period after activation, so make sure to check the displayed terms and expiry.' },
            { n: 6, icon: ShoppingBag, title: "Shop With the Merchant",
              body: "Tap Shop Now to continue to the participating merchant's website or app, or follow the applicable instructions for an eligible in-store offer. Complete your qualifying purchase according to the offer terms." },
            { n: 7, icon: PiggyBank, title: "Save & Earn",
              body: "Receive the applicable discount and, where offered, earn Cyannect Rewards on your eligible purchase." },
          ].map((s) => (
            <div key={s.n} className="pf-card p-5" data-testid={`how-step-${s.n}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 text-white font-semibold flex items-center justify-center shrink-0">{s.n}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <s.icon className="w-4 h-4 text-cyan-600" />
                    <div className="font-display text-slate-900 text-lg">{s.title}</div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mt-2">{s.body}</p>
                  {s.extra}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ================== MORE THAN COUPONS ================== */}
      <Section testid="more-than-coupons" tag="More Than Coupons">
        <SectionTitle>Save Now. Earn for Later.</SectionTitle>
        <p className="text-slate-600 max-w-3xl leading-relaxed mt-4">
          Cyannect is designed to give shoppers more than just access to discounts. With eligible offers, you can:
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: BadgePercent, title: "Save Instantly", body: "Activate participating merchant offers and receive applicable discounts on qualifying purchases." },
            { icon: PiggyBank, title: "Earn Cyannect Rewards", body: "Eligible purchases may earn rewards that accumulate in your Cyannect account." },
            { icon: Gift, title: "Redeem Your Rewards", body: "Once your Available Rewards reach the applicable minimum redemption amount, you can use eligible rewards toward digital gift cards available in the Cyannect Rewards catalogue." },
          ].map((t) => (
            <div key={t.title} className="pf-card p-6" data-testid={`mtc-${t.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <t.icon className="w-8 h-8 text-cyan-600" />
              <div className="font-display text-slate-900 text-lg mt-3">{t.title}</div>
              <p className="text-sm text-slate-600 leading-relaxed mt-2">{t.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ================== CYANNECT REWARDS ================== */}
      <Section testid="rewards" tag="Cyannect Rewards" tone="mint">
        <SectionTitle>Your Shopping Can Be More Rewarding.</SectionTitle>
        <p className="text-slate-600 max-w-3xl leading-relaxed mt-4">
          When you make an eligible purchase through a qualifying Cyannect offer, rewards may be added to your account. Your app keeps track of:
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Clock, title: "Pending Rewards", body: "Rewards associated with eligible purchases that are still being verified.", tone: "text-amber-600" },
            { icon: Wallet, title: "Available Rewards", body: "Approved rewards that are available for eligible redemption.", tone: "text-emerald-600" },
            { icon: TrendingUp, title: "Lifetime Rewards", body: "See how much you've earned through Cyannect over time.", tone: "text-violet-600" },
          ].map((t) => (
            <div key={t.title} className="pf-card p-6" data-testid={`rewards-${t.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <t.icon className={`w-8 h-8 ${t.tone}`} />
              <div className="font-display text-slate-900 text-lg mt-3">{t.title}</div>
              <p className="text-sm text-slate-600 leading-relaxed mt-2">{t.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ================== REDEEM FOR GIFT CARDS ================== */}
      <Section testid="giftcards" tag="Redeem Rewards for Gift Cards">
        <SectionTitle>Turn Your Rewards Into Something You'll Enjoy.</SectionTitle>
        <ol className="mt-6 space-y-3 max-w-3xl">
          {[
            "Once you have reached the required Available Rewards balance, open the Rewards section of the Cyannect App.",
            "Browse eligible digital gift cards available in the Rewards catalogue.",
            "Choose your preferred gift card and denomination.",
            "Confirm your redemption. Your digital gift card will then be made available according to the applicable fulfillment process.",
          ].map((t, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-cyan-500 text-white text-xs font-semibold flex items-center justify-center shrink-0">{i + 1}</div>
              <div className="text-sm text-slate-700 leading-relaxed flex-1">{t}</div>
            </li>
          ))}
        </ol>
        <FlowRow className="mt-8" items={[
          { icon: Award, label: "Earn" },
          { icon: TrendingUp, label: "Accumulate" },
          { icon: Gift, label: "Redeem" },
          { icon: CheckCircle2, label: "Enjoy" },
        ]} />
        <p className="text-xs text-slate-500 mt-4 max-w-3xl leading-relaxed">
          Gift-card brands, denominations and availability may vary by country, provider and time.
        </p>
      </Section>

      {/* ================== SHOP YOUR WAY ================== */}
      <Section testid="offer-types" tag="Shop Your Way" tone="mint">
        <SectionTitle>Different Stores. Different Offers.</SectionTitle>
        <p className="text-slate-600 max-w-3xl leading-relaxed mt-4">Participating merchants can provide several types of offers through Cyannect.</p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { title: "Product Offers", ex: "20% OFF Selected Running Shoes", body: "Save on a particular product." },
            { title: "Category Offers", ex: "15% OFF Jackets", body: "Discover savings across eligible products in a category." },
            { title: "Storewide Offers", ex: "10% OFF Eligible Store Purchases", body: "Save across qualifying purchases from a participating merchant." },
            { title: "Basket Offers", ex: "$25 OFF $200+", body: "Unlock savings after meeting an eligible purchase threshold." },
            { title: "Rewards Offers", ex: "Earn Cyannect Rewards on eligible purchase", body: "Earn Cyannect Rewards on qualifying purchases." },
            { title: "Combined Offers", ex: "Discount + Cyannect Rewards", body: "Where available, an offer may combine an eligible merchant discount with Cyannect Rewards." },
          ].map((t) => (
            <div key={t.title} className="pf-card p-5" data-testid={`offer-type-${t.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-cyan-600" />
                <div className="font-display text-slate-900 text-lg">{t.title}</div>
              </div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.body}</p>
              <div className="mt-3 text-xs pf-chip pf-chip-mint w-fit"><Sparkles className="w-3 h-3" /> Example: {t.ex}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ================== ONLINE & LOCAL ================== */}
      <Section testid="online-local" tag="Discover Online & Local Offers">
        <SectionTitle>Offers That Fit Where and How You Shop.</SectionTitle>
        <p className="text-slate-600 max-w-3xl leading-relaxed mt-4">Cyannect can support participating:</p>
        <div className="mt-6 flex flex-wrap gap-2 max-w-3xl">
          {["Online stores", "Retail businesses", "Restaurants", "Local merchants",
             "Service businesses", "E-commerce brands", "Multi-location businesses"].map((k) => (
            <span key={k} className="pf-chip pf-chip-cyan" data-testid={`type-${k.replace(/\s+/g, "-").toLowerCase()}`}>
              <MapPin className="w-3 h-3" /> {k}
            </span>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-6 max-w-3xl leading-relaxed">
          Offer availability can vary based on merchant participation, location, dates, products and eligibility requirements.
        </p>
      </Section>

      {/* ================== WHY SHOPPERS USE ================== */}
      <Section testid="why-shoppers" tag="Why Shoppers Use Cyannect" tone="mint">
        <SectionTitle>Real Reasons Shoppers Stick With Cyannect.</SectionTitle>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: Smartphone, title: "One App for Discovering Savings", body: "Explore offers from participating merchants without jumping between multiple coupon sources." },
            { icon: Store, title: "Multiple Offers From the Same Store", body: "Open a merchant storefront and browse its available offers in one place." },
            { icon: Zap, title: "Easy Offer Activation", body: "Choose the offer you want and activate it directly through Cyannect." },
            { icon: MapPin, title: "Relevant Offers", body: "Discover participating merchants and offers appropriate to applicable location and eligibility settings." },
            { icon: PiggyBank, title: "Rewards on Eligible Purchases", body: "Some qualifying purchases can earn Cyannect Rewards in addition to the applicable merchant savings." },
            { icon: Gift, title: "Gift Card Redemption", body: "Accumulate eligible Available Rewards and redeem them for available digital gift cards once you meet the applicable redemption requirements." },
          ].map((t) => (
            <div key={t.title} className="pf-card p-5" data-testid={`why-${t.title.split(" ")[0].toLowerCase()}`}>
              <t.icon className="w-6 h-6 text-cyan-600" />
              <div className="font-display text-slate-900 text-base mt-3">{t.title}</div>
              <p className="text-sm text-slate-600 leading-relaxed mt-2">{t.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ============= SHOPPERS USE THE APP ============= */}
      <Section testid="use-the-app" tag="Shoppers Use the App">
        <SectionTitle>Ready to Discover Your Next Offer?</SectionTitle>
        <p className="text-slate-600 max-w-3xl leading-relaxed mt-4">
          Cyannect.com is primarily the business platform used by participating brands, merchants and business partners. The shopping experience lives in the Cyannect App.
        </p>
        <p className="text-slate-600 mt-3 max-w-3xl">Download the app to:</p>
        <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-3xl">
          {[
            "Discover participating brands",
            "Browse merchant storefronts",
            "Find available offers",
            "Activate coupons",
            "Shop with participating merchants",
            "Track eligible rewards",
            "Redeem eligible rewards for available digital gift cards",
          ].map((k) => (
            <li key={k} className="flex items-center gap-2 text-sm text-slate-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> {k}
            </li>
          ))}
        </ul>
      </Section>

      {/* ================== DOWNLOAD ================== */}
      <Section testid="download" tag="Download Cyannect" tone="mint">
        <SectionTitle>Start Discovering Offers Today.</SectionTitle>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-[auto,1fr] gap-8 items-center">
          <QrCard qr={qr} large caption="Scan with your phone to download Cyannect." />
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              <StoreBadge platform="ios" testid="download-app-ios" />
              <StoreBadge platform="android" testid="download-app-android" />
            </div>
            <div className="text-sm text-slate-500">Available for iOS and Android.</div>
          </div>
        </div>
      </Section>

      {/* ================== FINAL CTA ================== */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-900 via-cyan-950 to-slate-900" />
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-white grid grid-cols-1 md:grid-cols-[1.15fr,auto] gap-10 items-center">
          <div>
            <div className="pf-chip pf-chip-cyan w-fit"><Sparkles className="w-3 h-3" /> Final CTA</div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl leading-tight text-white">
              Better Offers. Smarter Shopping. <span className="text-cyan-300">More Rewards.</span>
            </h2>
            <p className="mt-4 text-slate-300 max-w-xl">
              Discover participating merchants and their latest offers with Cyannect. Your next saving starts in the app.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <StoreBadge platform="ios" testid="final-app-ios" />
              <StoreBadge platform="android" testid="final-app-android" />
            </div>
          </div>
          <QrCard qr={qr} large caption="Download Cyannect" onDark />
        </div>
      </section>

      {/* ============ BUSINESS CROSS-LINK ============ */}
      <Section testid="business-cross" tag="Business Cross-Link">
        <SectionTitle>Are You a Brand or Business?</SectionTitle>
        <p className="text-slate-600 max-w-3xl leading-relaxed mt-4">
          Create your Cyannect Storefront, publish offers and connect with shoppers looking for ways to save.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/" className="pf-btn-glass px-5 py-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                data-testid="cross-cta-business">
            <Building2 className="w-3.5 h-3.5" /> Cyannect for Business
          </Link>
          <Link to="/register" className="pf-btn-primary px-5 py-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                data-testid="cross-cta-signup">
            <Handshake className="w-3.5 h-3.5" /> Merchant Sign Up
          </Link>
        </div>
      </Section>

      {/* ================== FAQ ================== */}
      <Section testid="faq" tag={t("faq_tag")} tone="mint">
        <SectionTitle>{t("faq_h2")}</SectionTitle>
        <div className="mt-6 space-y-3 max-w-3xl">
          {faq.map((f, i) => (
            <Faq key={i} q={f.q} a={f.a} testid={`faq-${i}`} />
          ))}
        </div>
      </Section>

      {/* Bottom padding so sticky CTA doesn't cover content */}
      <div className="pb-24 md:pb-8" />

      {/* Mobile sticky CTA */}
      <MobileStickyCta />
    </div>
  );
}

// ===================== helpers =====================

function Section({ children, tag, testid, tone = "plain" }) {
  const bg = tone === "mint" ? "bg-emerald-50/40" : "";
  return (
    <section className={`relative z-10 ${bg}`} data-testid={testid}>
      <div className="max-w-6xl mx-auto px-6 py-16">
        {tag && (
          <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold mb-3">
            {tag}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-slate-900">
      {children}
    </h2>
  );
}

function FlowRow({ items, className = "" }) {
  return (
    <div className={`mt-8 flex flex-wrap gap-4 items-center ${className}`} data-testid="flow-row">
      {items.map((it, i) => (
        <div key={it.label} className="flex items-center gap-3">
          <div className="flex flex-col items-center gap-2">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 text-white flex items-center justify-center">
              <it.icon className="w-5 h-5" />
            </div>
            <div className="text-xs font-semibold text-slate-700">{it.label}</div>
          </div>
          {i < items.length - 1 && <ArrowRight className="w-4 h-4 text-slate-400" />}
        </div>
      ))}
    </div>
  );
}

function QrCard({ qr, large = false, caption, onDark = false }) {
  const size = large ? "w-52 h-52" : "w-40 h-40";
  return (
    <div className={`rounded-2xl p-5 shadow-2xl shadow-cyan-500/20 flex flex-col items-center gap-3 ${onDark ? "bg-white" : "bg-white"}`}
         data-testid="qr-card">
      {qr ? (
        <img src={qr} alt="Scan to open the Cyannect app" className={size} data-testid="qr-image" />
      ) : (
        <div className={`${size} flex items-center justify-center text-slate-400`}
             data-testid="qr-fallback">
          <QrCode className="w-20 h-20" />
        </div>
      )}
      {caption && (
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold text-center max-w-[13rem]">
          {caption}
        </div>
      )}
      <div className="text-[11px] text-slate-400">app.cyannect.com</div>
    </div>
  );
}

function StoreBadge({ platform, small = false, testid }) {
  const isIos = platform === "ios";
  const Icon = isIos ? Apple : Play;
  const label = isIos ? "Download on the" : "Get it on";
  const store = isIos ? "App Store" : "Google Play";
  const pad = small ? "px-3 py-1.5" : "px-4 py-2.5";
  const iconSize = small ? "w-4 h-4" : "w-6 h-6";
  const labelSize = small ? "text-[8px]" : "text-[9px]";
  const storeSize = small ? "text-xs" : "text-base";
  return (
    <div
      className={`group inline-flex items-center gap-3 ${pad} rounded-xl bg-slate-900/90 border border-white/10 hover:border-cyan-400/40 hover:bg-slate-900 transition cursor-not-allowed relative overflow-hidden select-none text-white`}
      title="Coming soon"
      aria-disabled="true"
      data-testid={testid}
    >
      <Icon className={`${iconSize} shrink-0`} />
      <div className="text-left">
        <div className={`${labelSize} uppercase tracking-widest text-slate-400 font-semibold leading-none`}>{label}</div>
        <div className={`font-display ${storeSize} leading-tight`}>{store}</div>
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-slate-950/85 opacity-0 group-hover:opacity-100 transition text-[10px] uppercase tracking-widest font-semibold text-cyan-300">
        Coming soon
      </div>
    </div>
  );
}

function Faq({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pf-card overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen(!open)}
              className="w-full flex items-center justify-between gap-3 p-4 text-left"
              data-testid={`${testid}-toggle`}>
        <span className="font-semibold text-slate-900 text-sm">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed" data-testid={`${testid}-body`}>
          {a}
        </div>
      )}
    </div>
  );
}

const SHOPPER_FAQ = [
  { q: "Can I use Cyannect coupons directly on this website?",
    a: "Shopper coupon discovery and activation are provided through the Cyannect mobile app. Download the app to access available shopper offers." },
  { q: "Why do I need the Cyannect App?",
    a: "The app provides the consumer experience for discovering merchant storefronts, browsing offers, activating coupons, tracking eligible rewards and accessing reward redemptions." },
  { q: "Can a store have more than one offer?",
    a: "Yes. Participating merchants may publish multiple offers inside their Cyannect Storefront. Open the storefront to see the offers currently available to you." },
  { q: "What does \"Up to 25% OFF\" mean?",
    a: "It means an available offer from that merchant may provide savings of up to the displayed percentage. The highest discount may apply only to selected products, categories or qualifying purchases." },
  { q: "How do I activate an offer?",
    a: "Select an offer in the Cyannect App and tap Get Offer or Reveal Offer. Follow the displayed instructions and applicable terms." },
  { q: "Can offers expire?",
    a: "Yes. Offers may have fixed expiration dates or, where applicable, a limited redemption period after activation. Check the individual offer for details." },
  { q: "What are Cyannect Rewards?",
    a: "Cyannect Rewards may be earned on eligible purchases associated with qualifying offers. Rewards are subject to verification and applicable program terms." },
  { q: "When can I use my rewards?",
    a: "Once your Available Rewards reach the applicable minimum redemption requirement, eligible rewards may be redeemed for available digital gift cards through the Cyannect App." },
  { q: "Where can I see my rewards?",
    a: "Open the Rewards section of your Cyannect App to see Pending Rewards, Available Rewards, reward activity and eligible redemptions." },
  { q: "Are all offers available everywhere?",
    a: "No. Offer availability may depend on the merchant, product, location, campaign dates and other eligibility requirements." },
];

function MobileStickyCta() {
  const [installed, setInstalled] = useState(false);
  const { t } = useShoppersI18n();
  useEffect(() => {
    try {
      const standalone = window.matchMedia?.("(display-mode: standalone)").matches
                          || window.navigator.standalone === true;
      setInstalled(!!standalone);
    } catch { setInstalled(false); }
  }, []);
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-white/95 backdrop-blur border-t border-slate-200 shadow-2xl"
         data-testid="mobile-sticky-cta">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-slate-900">{t("sticky_title")}</div>
          <div className="text-[10px] text-slate-500">{t("sticky_sub")}</div>
        </div>
        <a href={APP_URL}
           className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap"
           data-testid={installed ? "sticky-open" : "sticky-download"}>
          {installed ? t("sticky_open") : t("sticky_download")}
        </a>
      </div>
    </div>
  );
}
